import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useNavigate,
	useParams,
	useSearch,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Maximize02,
} from "@untitledui/icons";
import { useMemo, useState } from "react";
import { DestinyEntityDrawer } from "@/components/Organisms/DestinyEntityDrawer/DestinyEntityDrawer";
import {
	DetailsState,
	DetailsTab,
} from "@/components/Organisms/Details/Details";
import { DrawerProvider } from "@/contexts/drawer-context";
import {
	AssetDetailsTab,
	AssetEntityDetails,
} from "@/features/assets/components";
import { getAssetDisplayTitle } from "@/features/assets/lib/get-asset-display-title.util";
import {
	getAssetWorkspacePath,
	getTicketRecordScopedAssetsTabPath,
} from "@/features/tickets/lib/get-asset-workspace-path.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDestinyDrawerCurrentTab } from "@/lib/use-destiny-drawer-current-tab";
import { assetInstanceQueryOptions } from "@/services/asset";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

export type AssetDrawerTab = "details" | "activity";

export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/workspace/records/$recordId/workspace/assets/$assetId/_drawer",
)({
	component: RouteComponent,
});

function RouteComponent() {
	return (
		<DrawerProvider>
			<AssetDrawer />
		</DrawerProvider>
	);
}

/**
 * Asset detail drawer nested under ticket → record Assets tab.
 * Workspace jumps to the ticket-scoped asset workspace.
 */
function AssetDrawer() {
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/workspace/records/$recordId/workspace/assets/$assetId/_drawer",
	});
	const search = useSearch({
		from: "/_authenticated/(web-app)/_web/web/$orgId/tickets/(ticket)/$ticketId/workspace/records/$recordId/workspace/assets",
		shouldThrow: false,
	});

	const [isOpen, setIsOpen] = useState(true);

	const currentTab = useDestinyDrawerCurrentTab(location.pathname);

	const assetQuery = useQuery({
		...assetInstanceQueryOptions.detail(params.assetId, {
			organizationId: params.orgId,
		}),
		retry: false,
	});

	const drawerMetadata = useMemo(() => {
		const asset = assetQuery.data;
		if (!asset) return [];

		const items: {
			id: string;
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (asset.assetNumber != null) {
			items.push({
				id: "asset-number",
				label: String(asset.assetNumber),
				icon: Hash01,
			});
		}
		if (asset.createdAt) {
			items.push({
				id: "created-at",
				label: createdMetadataLabel(t, asset.createdAt),
				icon: Calendar,
			});
		}
		if (asset.updatedAt) {
			items.push({
				id: "updated-at",
				label: updatedMetadataLabel(t, asset.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [assetQuery.data, t]);

	const searchQ = typeof search?.q === "string" ? search.q : undefined;

	const handleOpenWorkspace = () => {
		void navigate({
			href: getAssetWorkspacePath({
				orgId: params.orgId,
				ticketId: params.ticketId,
				assetId: params.assetId,
			}),
		});
	};

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			const tabPath = getTicketRecordScopedAssetsTabPath({
				orgId: params.orgId,
				ticketId: params.ticketId,
				recordId: params.recordId,
			});
			void navigate({
				href: searchQ ? `${tabPath}?q=${encodeURIComponent(searchQ)}` : tabPath,
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) {
			handleClose();
		}
	};

	const title = getAssetDisplayTitle({
		name: assetQuery.data?.name,
		assetNumber: assetQuery.data?.assetNumber,
	});

	const tabs = [
		{
			label:
				t("main.jobs.workspace.assets.drawer.tabs.details.label", {
					defaultValue: "Details",
				}) || "Details",
			id: AssetDetailsTab.Details,
		},
		{
			label:
				t("main.jobs.workspace.assets.drawer.tabs.activity.label", {
					defaultValue: "Activity",
				}) || "Activity",
			id: AssetDetailsTab.Activity,
		},
	];

	const handleTabChange = (tabId: string) => {
		if (tabId === currentTab) return;
		const to =
			tabId === DetailsTab.Activity
				? "/web/$orgId/tickets/$ticketId/workspace/records/$recordId/workspace/assets/$assetId/activity"
				: "/web/$orgId/tickets/$ticketId/workspace/records/$recordId/workspace/assets/$assetId/details";
		void navigate({
			to,
			params: {
				orgId: params.orgId,
				ticketId: params.ticketId,
				recordId: params.recordId,
				assetId: params.assetId,
			},
			search: { q: searchQ },
		});
	};

	return (
		<DestinyEntityDrawer
			type="asset"
			isOpen={isOpen}
			onOpenChange={handleOpenChange}
			title={title}
			metadata={drawerMetadata}
			isError={assetQuery.isError}
			errorDescription={
				t("main.jobs.workspace.assets.drawer.loadFailed", {
					defaultValue: "This asset could not be loaded.",
				}) || "This asset could not be loaded."
			}
			tabs={tabs}
			currentTab={currentTab}
			onTabChange={handleTabChange}
			data-testid="ticket-record-scoped-asset-drawer"
			actions={[
				{
					label:
						t("main.jobs.workspace.action", {
							defaultValue: "Workspace",
						}) || "Workspace",
					icon: Maximize02,
					color: "secondary",
					onPress: handleOpenWorkspace,
				},
			]}
			overflowActions={[]}
			details={
				<AssetEntityDetails
					orgId={params.orgId}
					assetId={params.assetId}
					state={
						assetQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					selectedTabId={currentTab}
					onTabChange={handleTabChange}
					tabs={tabs}
					className="max-md:[&_[role=tablist]]:hidden"
				/>
			}
			outlet={<Outlet />}
		>
			{() => null}
		</DestinyEntityDrawer>
	);
}
